/**
 * Boolean features only.
 * For features using other primitives, see ./BaseFeatures.ts
 */
export const BaseFeatureToggles = {
  LIMITS: 'LIMITS',
  COSTING_LIMITS: 'COSTING_LIMITS',
  TENANT: 'TENANT',
  UI_ORTHO_V2: 'UI_ORTHO_V2',
  SHIP_DATE_REPORTING: 'SHIP_DATE_REPORTING',
  CONTEXT_REPORTING: 'CONTEXT_REPORTING',
  RULE_SETS: 'RULE_SETS',
  CARRIER_MANUAL_MANIFEST: 'CARRIER_MANUAL_MANIFEST',
  AUTO_MANIFEST: 'AUTO_MANIFEST',
  DISPLAY_ACTUAL_PRICE: 'DISPLAY_ACTUAL_PRICE',
  RESTRICT_RATE_SHEET_ACCESS: 'RESTRICT_RATE_SHEET_ACCESS',
  UPS_VERIFY: 'UPS_VERIFY',
  PACKAGING_WORKFLOWS: 'PACKAGING_WORKFLOWS',
  RETURNS_FCTX: 'RETURNS_FCTX',
  SHIPMENT_LANES: 'SHIPMENT_LANES',
  DYNAMIC_LIMITS: 'DYNAMIC_LIMITS',
  DYNAMIC_LIMITS_GROUPS: 'DYNAMIC_LIMITS_GROUPS',
  CS_WEBHOOKS: 'CS_WEBHOOKS',
  LOCALIZE_CONSOLE: 'LOCALIZE_CONSOLE',
  LTL_SHIPMENTS: 'LTL_SHIPMENTS',
  SHOW_CUSTOMS_IN_PRINT_LABEL_TOOL: 'SHOW_CUSTOMS_IN_PRINT_LABEL_TOOL',
  OTS_CALCULATION: 'OTS_CALCULATION',
  TRACKING_REGISTRATION_DISABLED: 'TRACKING_REGISTRATION_DISABLED',
} as const;

export type BaseFeatureTogglesInterface = typeof BaseFeatureToggles;

export const baseFeatureTogglesHelpText: Record<keyof BaseFeatureTogglesInterface, string> = {
  LIMITS:
    'Partner is allowed to set daily limits for the number of shipments that go out for a given carrier and/or method at an origin. You need to set this to true for a V1 customer (UI_ORTHO_V2 is false). V2 customers see limits by default.',
  COSTING_LIMITS: 'Partner is allowed to set a single maximum daily limit for updgrade costs',
  TENANT:
    'Partner is allowed to create and scope assets to a tenant. If false, the global tenant filter, tenant worflows, and tenant linking steps will not appear in the UI',
  UI_ORTHO_V2: 'Toggle to show the V2 vs V1 UI. This does NOT change which version of the megamethod is in use',
  SHIP_DATE_REPORTING:
    'Allows for carrier selection reporting based on shipped date time of shipments instead of creation time of shipments',
  CONTEXT_REPORTING:
    'Determines whether or not to allow reporting aggregated by fulfilment context (based on which partners use it, we THINK this might just be for GME right now)',
  RULE_SETS: 'Toggle required to view rule-set pages (configure/rule-sets, configure/rule)',
  CARRIER_MANUAL_MANIFEST:
    'Toggle required to allow a button to be displayed in the Manual Manifest page. This button allows a user to perform a carrier manifest for a particular partner-tenant-origin combo. At this time, DHL is the only carrier requiring a carrier manifest. Soon, Pitney Bowes and Canada Post will also require manifesting. \nNote: This feature should eventually be enabled by default for all partners, thus negating the need to keep the toggle. The toggle was added to allow a controlled release of the feature.',
  AUTO_MANIFEST: 'Toggle required to replace manual manifest page with auto manifest page.',
  DISPLAY_ACTUAL_PRICE: 'Toggle to display actual cost instead of estimated cost for labels.',
  RESTRICT_RATE_SHEET_ACCESS:
    'Toggle to disable the modification and viewing of Rates and Rate Sheets that are confidential and not disclosed to customers.',
  UPS_VERIFY: 'Toggle to show/hide the UPS OAuth feature.',
  PACKAGING_WORKFLOWS: 'Show packaging and packaging set workflows to users',
  RETURNS_FCTX:
    'Temporary toggle to show/hide the RETURNS Fulfillment Context type. Needed for testing prior to rolling it out.',
  DYNAMIC_LIMITS:
    'Show dynamic limits (cost adjust config) on the count limits pages. Allows setting a target percentage of all shipments that should use a target carrier.',
  DYNAMIC_LIMITS_GROUPS:
    'Show limit scope selection on dynamic limit create/edit pages. Allows selecting specific origins/countries to apply the limit to. When disabled, defaults to all origins.',
  LTL_SHIPMENTS: 'Toggle to show/hide LTL Shipment search results.',
  SHOW_CUSTOMS_IN_PRINT_LABEL_TOOL:
    'Show the customs tab in the print label tool. This is meant to be temporary before we release to all customers',
  CS_WEBHOOKS: 'Toggle to enable Carrier Selection Webhooks',
  LOCALIZE_CONSOLE: 'Toggle to enable Localization of the Console',
  SHIPMENT_LANES: 'Toggle to enable Shipment Lanes',
  OTS_CALCULATION:
    'Allow this partner to use Shipium-calculated order-to-ship time. When enabled, the schedule entry form shows controls to let Shipium calculate the order-to-ship time (optionally using shipment tags) and treats the manual Expected Days To Ship value as a fallback.',
  TRACKING_REGISTRATION_DISABLED:
    "Stop registering this partner's shipments for tracking. Use it when their tracking is failing — expired or unentitled carrier credentials being the usual cause — so they stop generating tracking records that poll, fail and alert.\nThis stops NEW registrations only: shipments already registered keep polling until they expire on their own, so the existing noise decays rather than stopping. Leave it off for everyone else; tracking is registered by default.",
};
